import React, { useRef } from 'react';
import { FileText, Eye, EyeOff } from 'lucide-react';

/**
 * FooterEditor — admin editor for the optional "custom footer" text block.
 * The text is positionable anywhere on the page (x%, y%) and can be styled.
 */
const FooterEditor = ({
  footerText = '',
  footerX = 50,
  footerY = 95,
  footerVisible = true,
  footerFontSize = 14,
  footerColor = '#cbd5e1',
  onChange,
}) => {
  const canvasRef = useRef(null);
  const draggingRef = useRef(false);

  const update = (patch) => onChange && onChange(patch);

  const startDrag = (e) => {
    draggingRef.current = true;
    moveTo(e);
    e.preventDefault();
  };
  const moveTo = (e) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    let x = ((clientX - rect.left) / rect.width) * 100;
    let y = ((clientY - rect.top) / rect.height) * 100;
    x = Math.max(0, Math.min(100, x));
    y = Math.max(0, Math.min(100, y));
    update({ footer_x: Math.round(x * 10) / 10, footer_y: Math.round(y * 10) / 10 });
  };
  const onMove = (e) => {
    if (!draggingRef.current) return;
    moveTo(e);
  };
  const endDrag = () => {
    draggingRef.current = false;
  };

  return (
    <div className="admin-section" data-testid="footer-editor-section">
      <h3 className="admin-section-title flex items-center gap-2">
        <FileText size={18} className="text-yellow-400" /> Cədvəlin altında mətn
      </h3>
      <p className="admin-section-desc">
        Səhifədə hər hansı yerə yerləşdirilə bilən sərbəst mətn bloku. Mətni yazın, ölçüsünü/rəngini seçin,
        sonra aşağıdakı önizləmədə istənilən yerə sürükləyin.
      </p>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={`admin-btn-secondary ${footerVisible ? '' : 'opacity-60'}`}
            onClick={() => update({ footer_visible: !footerVisible })}
            data-testid="footer-visible-toggle"
          >
            {footerVisible ? <Eye size={14} /> : <EyeOff size={14} />}
            <span className="ml-1">{footerVisible ? 'Görünür' : 'Gizli'}</span>
          </button>
          <span className="text-xs text-white/40">Mətn səhifədə göstərilsin/gizlədilsin</span>
        </div>

        <div>
          <label className="title-editor-label">Mətn (çoxsətirli)</label>
          <textarea
            className="karma-input text-left w-full"
            value={footerText}
            onChange={(e) => update({ footer_text: e.target.value })}
            maxLength={1000}
            rows={4}
            placeholder="Burada qeyd, izah və ya istənilən mətn yazın..."
            data-testid="footer-text-input"
            style={{ resize: 'vertical', minHeight: 96, fontFamily: 'inherit' }}
          />
          <span className="title-editor-hint">{footerText.length}/1000 — yeni sətir üçün Enter</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="flex items-center gap-2">
            <span className="text-sm text-white/80 w-32">Ölçü: {footerFontSize}px</span>
            <input
              type="range" min="8" max="60" step="1" value={footerFontSize}
              onChange={(e) => update({ footer_font_size: parseInt(e.target.value, 10) })}
              className="flex-1"
              data-testid="footer-font-size"
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-sm text-white/80 w-20">Rəng:</span>
            <input
              type="color"
              value={footerColor}
              onChange={(e) => update({ footer_color: e.target.value })}
              className="h-9 w-16 rounded border border-white/15"
              data-testid="footer-color"
            />
          </label>
          <div className="flex items-center gap-2 text-xs text-white/50">
            <span>X: {footerX.toFixed(1)}%</span>
            <span>Y: {footerY.toFixed(1)}%</span>
          </div>
        </div>

        {/* Position canvas — 9:14 aspect rough page preview */}
        <div>
          <label className="title-editor-label">Mövqe — mətni klikləyib sürükləyin</label>
          <div
            ref={canvasRef}
            onMouseDown={startDrag}
            onMouseMove={onMove}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            onTouchStart={startDrag}
            onTouchMove={onMove}
            onTouchEnd={endDrag}
            data-testid="footer-position-canvas"
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '16 / 10',
              background: 'linear-gradient(180deg, rgba(76,29,149,0.15), rgba(15,23,42,0.7))',
              border: '1.5px dashed rgba(168,85,247,0.4)',
              borderRadius: 12,
              overflow: 'hidden',
              cursor: 'crosshair',
              userSelect: 'none',
            }}
          >
            {/* Faux page elements */}
            <div style={{ position: 'absolute', top: '8%', left: '50%', transform: 'translateX(-50%)',
              color: 'rgba(255,255,255,0.45)', fontSize: 11, fontFamily: "'Cinzel', serif" }}>
              ◯ Matris zonası
            </div>
            <div style={{ position: 'absolute', top: '40%', left: '20%', right: '20%', height: '20%',
              background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 8 }} />
            <div style={{ position: 'absolute', top: '70%', left: '15%', right: '15%', height: '10%',
              background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 8,
              fontSize: 10, color: 'rgba(255,255,255,0.3)', textAlign: 'center', lineHeight: '24px' }}>
              ▦ Cədvəl
            </div>
            {/* Footer marker */}
            <div
              style={{
                position: 'absolute',
                left: `${footerX}%`,
                top: `${footerY}%`,
                transform: 'translate(-50%, -50%)',
                padding: '6px 10px',
                background: 'rgba(251, 191, 36, 0.18)',
                border: '1.5px solid #fbbf24',
                borderRadius: 6,
                fontSize: 11,
                color: '#fde68a',
                fontWeight: 700,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
                maxWidth: '85%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                boxShadow: '0 0 12px rgba(251,191,36,0.4)',
              }}
            >
              {footerText.split('\n')[0].slice(0, 36) || 'Mətn buraya...'}
            </div>
          </div>
          <p className="text-xs text-white/40 mt-1">
            X: 0=sol, 100=sağ • Y: 0=yuxarı, 100=alt, 100+=alt boşluq (uzun səhifələrdə).
          </p>
        </div>

        {/* Preset positions */}
        <div className="flex flex-wrap gap-2">
          <button type="button" className="admin-btn-secondary text-xs"
            onClick={() => update({ footer_x: 50, footer_y: 95 })}>↓ Aşağı mərkəz</button>
          <button type="button" className="admin-btn-secondary text-xs"
            onClick={() => update({ footer_x: 50, footer_y: 50 })}>● Mərkəz</button>
          <button type="button" className="admin-btn-secondary text-xs"
            onClick={() => update({ footer_x: 15, footer_y: 95 })}>↙ Sol-alt</button>
          <button type="button" className="admin-btn-secondary text-xs"
            onClick={() => update({ footer_x: 85, footer_y: 95 })}>↘ Sağ-alt</button>
          <button type="button" className="admin-btn-secondary text-xs"
            onClick={() => update({ footer_x: 50, footer_y: 8 })}>↑ Yuxarı</button>
        </div>
      </div>
    </div>
  );
};

export default FooterEditor;
